// BL-177 Gate 17d — Action Pill Standard v1. The one component for every action pill or action
// button that opens a submenu. Do not hand-roll a flyout; use this.
//
// Look: each option is its own pill (the therideatl.com contact launcher), stacked 8px apart.
// Timing: the same dwell, grace period and cascade as the Communications status-icon menu — both
// read src/lib/uiMotion.js. Behavior: a click on the parent pill keeps the parent's own action;
// hover (250ms), Enter/ArrowDown, or a touch long-press opens the menu; arrows move, Escape and a
// click outside close.
// Gate 17e (v1.01): only one menu is open at a time app-wide (src/lib/menuCoordinator.js), the
// menu fades out over 100ms when it closes, and the parent pill holds one steady look while open.
// Standard: Docs/Build_Execution/Platform/IntelAgent_Action_Pill_Standard_v1_01.md
//
// Options: [{ key, label, onSelect, icon?, iconClassName?, badge?: { text, className? },
//             disabled?, active?, title?, keepOpen?, displayOnly?, href?, options?: [...nested], placement?, mobilePlacement? }]

import React, { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { actionPillCssVars } from './actionPillTokens';
import { claimOpenMenu } from '../../lib/menuCoordinator';
import {
    cascadeDelayMs,
    LONG_PRESS_MS,
    MENU_CLOSE_DWELL_MS,
    MENU_CLOSE_FADE_MS,
    MENU_ITEM_RISE_EASING,
    MENU_ITEM_RISE_MS,
    MENU_OPEN_DWELL_MS,
} from '../../lib/uiMotion';
import { MENU_OPEN_CLICK, useCurrentMenuOpensPreference } from '../../lib/menuOpenPreference';

// The id of the menu an option lives in, so a nested menu can tell the coordinator its parent.
const ParentMenuContext = createContext(null);

const OPEN_KEYS = {
    parent: ['Enter', 'ArrowDown'],
    right: ['Enter', 'ArrowRight'],
    left: ['Enter', 'ArrowLeft'],
};
const BACK_KEY = { right: 'ArrowLeft', left: 'ArrowRight' };

// Review fix round 1 (1.12.212-beta): a side submenu keeps this gap from the screen edge, and never gets narrower than this.
const SUBMENU_EDGE_GUTTER = 8;
const SUBMENU_MIN_WIDTH = 120;

// The same touch check the hover-open path has always used.
const isTouchDevice = () => typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;

function ActionPillMenu({
    options = [],
    menuLabel = 'Actions',
    children,
    triggerClassName = '',
    triggerLabel,
    triggerTitle,
    triggerProps = {},
    triggerVariant = 'parent',
    onTriggerClick,
    disabled = false,
    menuDisabled = false,
    placement = 'bottom',
    mobilePlacement,
    align = 'start',
    size = 'default',
    open: openProp,
    onOpenChange,
    className = '',
    wrapperProps = {},
    menuClassName = '',
    scrollable = false,
    emptyLabel = '',
    onItemChosen,
    initialFocusKey,
}) {
    const menuId = `ia-pill-${useId().replace(/:/g, '')}`;
    const menuOpensOn = useCurrentMenuOpensPreference();
    const parentMenu = useContext(ParentMenuContext);
    const parentMenuId = parentMenu?.id;
    const controlled = openProp !== undefined;
    const [openState, setOpenState] = useState(false);
    const [revealed, setRevealed] = useState(false);
    const wrapperRef = useRef(null);
    const preferredPlacement = useCallback(() => {
        if (!mobilePlacement) return placement;
        const containerWidth = parentMenuId ? 0 : wrapperRef.current?.parentElement?.getBoundingClientRect?.().width || 0;
        const narrow = typeof window !== 'undefined' && (window.innerWidth < 768 || (containerWidth > 0 && containerWidth < 520));
        return narrow ? mobilePlacement : placement;
    }, [mobilePlacement, placement, parentMenuId]);
    const [responsivePlacement, setResponsivePlacement] = useState(preferredPlacement);
    const triggerRef = useRef(null);
    const rootTriggerRef = parentMenu?.rootTriggerRef || triggerRef;
    const rootPlacement = parentMenu?.rootPlacement || placement;
    // The side the parent submenu opened on (null when the parent is the top-level column).
    const inheritedSide = parentMenu?.side || null;
    const menuRef = useRef(null);
    const dwellTimerRef = useRef(null);
    const closeTimerRef = useRef(null);
    const longPressTimerRef = useRef(null);
    const longPressFiredRef = useRef(false);
    const focusOnOpenRef = useRef(false);
    const instantCloseRef = useRef(false);
    const lastPointerTypeRef = useRef(null);
    const menuAvailable = !menuDisabled && !disabled;
    const open = Boolean((controlled ? openProp : openState) && menuAvailable && parentMenu?.open !== false);
    const openRef = useRef(open);
    openRef.current = open;
    // Closing fades the whole menu out at once (MENU_CLOSE_FADE_MS) — unless another menu opening
    // forced it shut, which is instant. Derived during render so the fading menu never unmounts
    // for a frame first.
    const [closing, setClosing] = useState(false);
    const [prevOpen, setPrevOpen] = useState(open);
    if (prevOpen !== open) {
        setPrevOpen(open);
        setClosing(!open && !instantCloseRef.current);
    }
    const rendered = open || closing;
    const nested = triggerVariant === 'option';
    const side = placement === 'left' || placement === 'right' ? placement : null;
    const openKeys = side
        ? [...OPEN_KEYS.parent, ...OPEN_KEYS[side]]
        : placement === 'right' ? [...OPEN_KEYS.parent, 'ArrowRight'] : OPEN_KEYS.parent;
    const nearestIsLast = placement === 'top';

    const clearTimer = (ref) => {
        if (!ref.current) return;
        clearTimeout(ref.current);
        ref.current = null;
    };

    const setOpen = useCallback((next) => {
        if (!controlled) setOpenState(next);
        onOpenChange?.(next);
    }, [controlled, onOpenChange]);

    useEffect(()=>{if(parentMenu?.open===false)setOpen(false);},[parentMenu?.open,setOpen]);
    const closeMenu = useCallback(({ refocus = false } = {}) => {
        clearTimer(dwellTimerRef);
        clearTimer(closeTimerRef);
        if (openRef.current) setOpen(false);
        if (refocus) triggerRef.current?.focus();
    }, [setOpen]);

    const openWithFocus = () => {
        if (!menuAvailable) return;
        clearTimer(dwellTimerRef);
        clearTimer(closeTimerRef);
        focusOnOpenRef.current = true;
        if (openRef.current) focusNearest();
        else setOpen(true);
    };

    const ownItems = () => Array.from(menuRef.current?.querySelectorAll(`[data-ia-pill-item="${menuId}"]:not(:disabled)`) || []);

    // Keyboard open lands on `initialFocusKey` when that option is enabled, otherwise on the
    // option nearest the parent pill.
    const focusNearest = () => {
        const items = ownItems();
        const preferred = initialFocusKey === undefined || initialFocusKey === null
            ? null
            : items.find((item) => item.getAttribute('data-ia-pill-key') === String(initialFocusKey));
        (preferred || (nearestIsLast ? items[items.length - 1] : items[0]))?.focus();
    };

    useEffect(() => {
        if (!open) {
            instantCloseRef.current = false;
            return undefined;
        }
        if (scrollable && nearestIsLast && menuRef.current) {
            menuRef.current.scrollTop = menuRef.current.scrollHeight;
        }
        if (focusOnOpenRef.current) {
            focusOnOpenRef.current = false;
            focusNearest();
        }
        const frame = requestAnimationFrame(() => setRevealed(true));
        return () => cancelAnimationFrame(frame);
        // focusNearest and the initial scroll only read refs and props.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!closing) return undefined;
        const timer = setTimeout(() => setClosing(false), MENU_CLOSE_FADE_MS);
        return () => clearTimeout(timer);
    }, [closing]);

    // Fully gone: the next open starts its cascade from hidden again.
    useEffect(() => {
        if (!rendered) setRevealed(false);
    }, [rendered]);

    // One menu open at a time, app-wide: opening this one shuts every other (except this menu's
    // own parents) instantly. Layout effect so the other menu is gone before the browser paints.
    const forceCloseRef = useRef(() => {});
    forceCloseRef.current = () => {
        clearTimer(dwellTimerRef);
        clearTimer(closeTimerRef);
        instantCloseRef.current = true;
        setClosing(false);
        if (openRef.current) setOpen(false);
    };
    useLayoutEffect(() => {
        if (!rendered) return undefined;
        return claimOpenMenu(menuId, { parentId: parentMenuId, close: () => forceCloseRef.current() });
    }, [rendered, menuId, parentMenuId]);

    useLayoutEffect(() => {
        if (!rendered) return undefined;
        const fitMenu = () => {
            const menu = menuRef.current;
            const trigger = triggerRef.current;
            if (!menu || !trigger) return;
            let left = 0;
            let right = document.documentElement.clientWidth || window.innerWidth;
            for (let ancestor = wrapperRef.current?.parentElement; ancestor; ancestor = ancestor.parentElement) {
                if (!/hidden|clip|auto|scroll/.test(getComputedStyle(ancestor).overflowX)) continue;
                const bounds = ancestor.getBoundingClientRect();
                left = Math.max(left, bounds.left);
                right = Math.min(right, bounds.right);
            }
            const triggerBounds = trigger.getBoundingClientRect();
            // Each submenu anchors to its own trigger, not to the widest pill in the parent column.
            const cascadeRight = triggerBounds.right;
            const cascadeLeft = triggerBounds.left;
            const requestedPlacement = preferredPlacement();
            const horizontalMenuWidth = menuClassName.includes('communications-filter-menu')
                ? Array.from(menu.children).reduce((width, item) => width + item.getBoundingClientRect().width, 0)
                    + Math.max(0, menu.children.length - 1) * (Number.parseFloat(getComputedStyle(menu).gap) || 0)
                    + 32
                : menu.scrollWidth;
            // Val's rule (1.12.212-beta): only the top level stacks vertically. Every submenu opens SIDEWAYS, in the row of
            // the pill that opened it — to the right when it fits, else to the left; never as more rows in the parent's
            // column. A nested `top`/`bottom` request (e.g. a phone-width mobilePlacement) is read as "beside", too.
            // When neither side has room for the whole submenu, it takes the roomier side and its labels shorten.
            let nextPlacement;
            let sideRoom = null;
            if (parentMenuId && rootPlacement === 'top' && rootTriggerRef.current?.closest('[data-shared-message-composer]')) {
                nextPlacement = 'top';
            } else if (parentMenuId) {
                const roomRight = right - cascadeRight;
                const roomLeft = cascadeLeft - left;
                const room = { right: roomRight, left: roomLeft };
                if (inheritedSide) {
                    // A deeper level keeps going the way its parent submenu went: turning back would cover the column
                    // that opened the parent (SMS → Templates → Edit templates all read left to right).
                    nextPlacement = inheritedSide;
                } else {
                    const wanted = requestedPlacement === 'left' ? 'left' : 'right';
                    const other = wanted === 'right' ? 'left' : 'right';
                    if (room[wanted] >= horizontalMenuWidth) nextPlacement = wanted;
                    else if (room[other] >= horizontalMenuWidth) nextPlacement = other;
                    else nextPlacement = room[wanted] >= room[other] ? wanted : other;
                }
                // Review fix round 1: keep a small gap from the screen edge (the 390 px check had Templates flush at 390).
                sideRoom = Math.max(0, room[nextPlacement] - SUBMENU_EDGE_GUTTER);
            } else {
                nextPlacement = requestedPlacement === 'right'
                    && right - triggerBounds.right < horizontalMenuWidth
                    ? 'bottom'
                    : requestedPlacement;
            }
            if (responsivePlacement !== nextPlacement) {
                setResponsivePlacement(nextPlacement);
                return;
            }
            const filterCascade = menuClassName.includes('communications-filter-menu') && responsivePlacement === 'right';
            const availableWidth = filterCascade ? right - triggerBounds.right : right - left;
            // Review fix round 1: a submenu never shrinks below a usable width. With less room than that beside its parent it
            // stays in the same row but slides back over the parent column (clamped on screen) — still sideways, never as rows.
            const squeezed = sideRoom !== null && sideRoom < SUBMENU_MIN_WIDTH;
            menu.style.maxWidth = squeezed
                ? `min(26rem, ${Math.max(0, right - left - 2 * SUBMENU_EDGE_GUTTER)}px)`
                : sideRoom !== null
                ? `min(26rem, ${sideRoom}px)`
                : filterCascade
                    ? `min(80vw, ${Math.max(0, availableWidth)}px)`
                    : `min(26rem, ${Math.max(0, availableWidth)}px)`;
            menu.style.transform = '';
            const bounds = menu.getBoundingClientRect();
            const cascadeStart = parentMenuId && nextPlacement === 'right'
                ? Math.max(bounds.left, cascadeRight)
                : parentMenuId && nextPlacement === 'left'
                    ? Math.min(bounds.left, cascadeLeft - bounds.width) : bounds.left;
            // A submenu is not pulled back over its own parent column while it fits beside it (see `squeezed` above).
            const fittedLeft = parentMenuId && !squeezed
                ? cascadeStart
                : parentMenuId
                    ? Math.min(Math.max(cascadeStart, left + SUBMENU_EDGE_GUTTER), Math.max(left, right - SUBMENU_EDGE_GUTTER - bounds.width))
                    : Math.min(Math.max(cascadeStart, left), Math.max(left, right - bounds.width));
            const bottom = parentMenuId && rootPlacement === 'top'
                ? Math.min(window.innerHeight, rootTriggerRef.current?.getBoundingClientRect().top ?? window.innerHeight)
                : window.innerHeight;
            // Fit the pills, not the invisible padding under them: counting the box's bottom padding lifted a
            // one-row submenu above its parent pill's row.
            const contentHeight = bounds.height - (Number.parseFloat(getComputedStyle(menu).paddingBottom) || 0);
            const fittedTop = parentMenuId ? Math.max(0, Math.min(bounds.top, bottom - contentHeight)) : bounds.top;
            menu.style.transform = `translate(${fittedLeft - bounds.left}px, ${fittedTop - bounds.top}px)`;
        };
        fitMenu();
        window.addEventListener('resize', fitMenu);
        window.addEventListener('scroll', fitMenu, true);
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fitMenu);
        if (menuRef.current) observer?.observe(menuRef.current);
        if (wrapperRef.current) observer?.observe(wrapperRef.current);
        if (wrapperRef.current?.parentElement) observer?.observe(wrapperRef.current.parentElement);
        return () => {
            window.removeEventListener('resize', fitMenu);
            window.removeEventListener('scroll', fitMenu, true);
            observer?.disconnect();
        };
    }, [rendered, placement, mobilePlacement, responsivePlacement, align, options.length, menuClassName, preferredPlacement, parentMenuId, inheritedSide, rootTriggerRef, rootPlacement]);

    useLayoutEffect(() => {
        if (!rendered) {
            setResponsivePlacement(preferredPlacement());
        }
    }, [rendered, placement, mobilePlacement, preferredPlacement]);

    useEffect(() => {
        const syncPlacement = () => setResponsivePlacement(preferredPlacement());
        window.addEventListener('resize', syncPlacement);
        return () => window.removeEventListener('resize', syncPlacement);
    }, [placement, mobilePlacement, preferredPlacement]);

    // Keyboard and long-press opens have no pointer-leave to close them, so a click or tap outside
    // (or Escape anywhere) closes the menu.
    useEffect(() => {
        if (!open) return undefined;
        const handleOutside = (event) => {
            if (!wrapperRef.current?.contains(event.target)) closeMenu();
        };
        const handleEscape = (event) => {
            if (event.key === 'Escape') closeMenu();
        };
        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('touchstart', handleOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleOutside);
            document.removeEventListener('touchstart', handleOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [open, closeMenu]);

    useEffect(() => () => {
        clearTimer(dwellTimerRef);
        clearTimer(closeTimerRef);
        clearTimer(longPressTimerRef);
    }, []);

    const handlePointerEnter = (event) => {
        wrapperProps.onMouseEnter?.(event);
        clearTimer(closeTimerRef);
        const touchDevice = isTouchDevice();
        // Locked hotfix: only the composer's Templates chain (the submenus inside the channel picker) opens on hover under a
        // click preference. Send ▾, Signature and the channel picker itself keep click-to-open.
        const templatesHover = Boolean(parentMenuId && wrapperRef.current?.closest('[data-message-type-select]'));
        if (!menuAvailable || (menuOpensOn === MENU_OPEN_CLICK && !templatesHover) || touchDevice || openRef.current || dwellTimerRef.current) return;
        dwellTimerRef.current = setTimeout(() => {
            dwellTimerRef.current = null;
            setOpen(true);
        }, MENU_OPEN_DWELL_MS);
    };

    const handlePointerLeave = (event) => {
        wrapperProps.onMouseLeave?.(event);
        clearTimer(dwellTimerRef);
        // A touch tap fires a compatibility mouseleave right after it. On touch, a menu closes by a tap outside, Escape or a
        // choice, never by the pointer "leaving" (that timer collapsed the composer submenus before the next tap).
        if (isTouchDevice() || lastPointerTypeRef.current === 'touch') return;
        if (!openRef.current || closeTimerRef.current) return;
        closeTimerRef.current = setTimeout(() => {
            closeTimerRef.current = null;
            setOpen(false);
        }, MENU_CLOSE_DWELL_MS);
    };

    const handleWrapperPointerDown = (event) => {
        wrapperProps.onPointerDownCapture?.(event);
        lastPointerTypeRef.current = event.pointerType || null;
    };

    const handleWrapperBlur = (event) => {
        wrapperProps.onBlur?.(event);
        // Tabbing to another element closes the menu; focus dropping to nothing (null, or the
        // document itself) doesn't.
        const next = event.relatedTarget;
        if (next?.nodeType === 1 && !wrapperRef.current?.contains(next)) closeMenu();
    };

    const handleTriggerClick = (event) => {
        triggerProps.onClick?.(event);
        clearTimer(dwellTimerRef);
        clearTimer(closeTimerRef);
        if (onTriggerClick) {
            onTriggerClick(event);
            return;
        }
        if (!menuAvailable) return;
        setOpen(!openRef.current);
    };

    const handleTriggerKeyDown = (event) => {
        triggerProps.onKeyDown?.(event);
        if (!menuAvailable) return;
        if (openKeys.includes(event.key)) {
            event.preventDefault();
            event.stopPropagation();
            openWithFocus();
            return;
        }
        if (event.key === 'Escape' && openRef.current) {
            event.stopPropagation();
            closeMenu();
        }
    };

    const longPressHandlers = menuAvailable ? {
        onTouchStart: (event) => {
            triggerProps.onTouchStart?.(event);
            longPressFiredRef.current = false;
            clearTimer(longPressTimerRef);
            longPressTimerRef.current = setTimeout(() => {
                longPressTimerRef.current = null;
                longPressFiredRef.current = true;
                openWithFocus();
            }, LONG_PRESS_MS);
        },
        onTouchEnd: (event) => {
            clearTimer(longPressTimerRef);
            if (longPressFiredRef.current) {
                // The long-press opened the menu; cancelling the touchend stops the browser also
                // turning the press into a click on the parent pill.
                longPressFiredRef.current = false;
                event.preventDefault();
            }
        },
        onTouchMove: () => clearTimer(longPressTimerRef),
        onTouchCancel: () => {
            clearTimer(longPressTimerRef);
            longPressFiredRef.current = false;
        },
        onContextMenu: (event) => event.preventDefault(),
    } : {};

    // The side a submenu actually opened on (it may have flipped left for room); its "back" arrow points home.
    const openSide = responsivePlacement === 'left' || responsivePlacement === 'right' ? responsivePlacement : side;
    const handleMenuKeyDown = (event) => {
        const items = ownItems();
        const index = items.indexOf(document.activeElement);
        let next = null;
        if (event.key === 'ArrowDown') next = items[(index + 1) % items.length];
        if (event.key === 'ArrowUp') next = items[(index - 1 + items.length) % items.length];
        if (event.key === 'Home') next = items[0];
        if (event.key === 'End') next = items[items.length - 1];
        if (next) {
            event.preventDefault();
            event.stopPropagation();
            next.focus();
            return;
        }
        if (event.key === 'Escape' || (nested && openSide && event.key === BACK_KEY[openSide])) {
            event.preventDefault();
            event.stopPropagation();
            closeMenu({ refocus: true });
        }
    };

    const choose = (option, event) => {
        event.stopPropagation();
        if (option.disabled) return;
        option.onSelect?.(option, event);
        if (option.keepOpen) return;
        closeMenu();
        onItemChosen?.();
    };

    // Gate 17e: no hand-off any more. While the menu is open the parent pill holds one steady look
    // whatever the pointer does among the options (see the global.css rules), so nothing flickers.
    // The parent's own `hover:` utilities are set aside while open too, so the pointer crossing back
    // over the parent can't toggle its background either.
    const baseTriggerClassName = typeof triggerClassName === 'function'
        ? triggerClassName({ open })
        : triggerClassName;
    const resolvedTriggerClassName = open
        ? String(baseTriggerClassName || '').split(/\s+/).filter((cls) => !cls.startsWith('hover:')).join(' ')
        : baseTriggerClassName;
    const {
        onClick: _onClick, onKeyDown: _onKeyDown, onTouchStart: _onTouchStart,
        style: triggerStyle = {}, ...restTriggerProps
    } = triggerProps;
    const {
        onMouseEnter: _enter, onMouseLeave: _leave, onBlur: _blur, onPointerDownCapture: _pointerDown, className: wrapperExtraCls = '',
        style: wrapperStyle = {}, ...restWrapperProps
    } = wrapperProps;

    const itemState = (option, index) => {
        const delay = revealed ? cascadeDelayMs(index, options.length, { nearestIsLast }) : 0;
        return {
            'data-ia-pill-item': menuId,
            'data-ia-pill-key': option.key,
            'data-revealed': revealed ? 'true' : 'false',
            'data-has-icon': option.icon ? 'true' : 'false',
            'data-active': option.active ? 'true' : undefined,
            // Fading out: drop out of the accessibility tree and the tab order at once.
            ...(open ? {} : { role: undefined, tabIndex: -1 }),
            style: {
                transitionDuration: `${MENU_ITEM_RISE_MS}ms`,
                transitionTimingFunction: MENU_ITEM_RISE_EASING,
                // The cascade delay is for the reveal only (opacity, transform — the first two
                // transitioned properties); a hover colour change never waits behind it.
                transitionDelay: `${delay}ms, ${delay}ms, 0ms`,
            },
        };
    };

    const optionContent = (option) => (
        <>
            {option.icon && (
                <span aria-hidden="true" className={`ia-action-pill-icon ${option.iconClassName || ''}`}>{option.icon}</span>
            )}
            <span className="ia-action-pill-label">{option.label}</span>
            {option.badge && (
                <span className={`ia-action-pill-badge ${option.badge.className || ''}`}>{option.badge.text}</span>
            )}
        </>
    );

    const renderOption = (option, index) => {
        // ia-hover-pulse-off: option pills carry their own ring, not a workspace-wide button pulse.
        const optionCls = `ia-action-pill-option ia-hover-pulse-off ${option.className || ''}`;
        if (option.displayOnly) {
            const { 'data-ia-pill-item': _item, 'data-ia-pill-key': _key, ...state } = itemState(option, index);
            return (
                <span key={option.key} className={`${optionCls} ia-action-pill-display`} title={option.title} {...state}>
                    {optionContent(option)}
                </span>
            );
        }
        if (Array.isArray(option.options)) {
            const state = itemState(option, index);
            return (
                <ActionPillMenu
                    key={option.key}
                    options={option.options}
                    menuLabel={option.label}
                    triggerVariant="option"
                    triggerClassName={optionCls}
                    triggerTitle={option.title}
                    triggerProps={{ role: 'menuitem', ...state, onClick: option.onSelect ? (event)=>option.onSelect(option,event) : undefined }}
                    disabled={option.disabled}
                    placement={option.placement || 'right'}
                    mobilePlacement={option.mobilePlacement}
                    size={size}
                    scrollable={option.scrollable}
                    wrapperProps={{ className: 'ia-action-pill-nested' }}
                    onItemChosen={() => {
                        closeMenu();
                        onItemChosen?.();
                    }}
                >
                    {optionContent(option)}
                </ActionPillMenu>
            );
        }
        const Item = option.href && !option.disabled ? 'a' : 'button';
        return (
            <Item
                key={option.key}
                {...(Item === 'a' ? { href: option.href } : { type: 'button', disabled: option.disabled })}
                role="menuitem"
                title={option.title}
                className={optionCls}
                onClick={(event) => choose(option, event)}
                {...itemState(option, index)}
            >
                {optionContent(option)}
            </Item>
        );
    };

    return (
        <div
            ref={wrapperRef}
            className={`ia-action-pill-launcher relative inline-flex ${rendered ? 'z-50' : ''} ${wrapperExtraCls} ${className}`}
            {...restWrapperProps}
            style={{
                ...wrapperStyle,
            }}
            onMouseEnter={handlePointerEnter}
            onMouseLeave={handlePointerLeave}
            onPointerDownCapture={handleWrapperPointerDown}
            onBlur={handleWrapperBlur}
        >
            <button
                ref={triggerRef}
                type="button"
                disabled={disabled}
                aria-label={triggerLabel}
                title={triggerTitle}
                {...restTriggerProps}
                {...(menuDisabled ? {} : { 'aria-haspopup': 'menu', 'aria-expanded': open, 'aria-controls': open ? menuId : undefined })}
                data-open={open ? 'true' : 'false'}
                className={`${nested ? '' : 'ia-action-pill-trigger'} ${resolvedTriggerClassName}`}
                style={{
                    ...triggerStyle,
                }}
                onClick={handleTriggerClick}
                onKeyDown={handleTriggerKeyDown}
                {...longPressHandlers}
            >
                {children}
            </button>
            {rendered && (
                // The menu box (padding included) is the hover bridge: the gaps between option
                // pills and the space back to the parent are inside it, so the pointer never
                // "leaves" while it moves between options.
                <div
                    ref={menuRef}
                    {...(open
                        ? { id: menuId, role: 'menu', 'aria-label': menuLabel }
                        : { 'aria-hidden': 'true', inert: '' })}
                    className={`ia-action-pill-menu ${menuClassName}`}
                    data-placement={responsivePlacement}
                    data-align={align}
                    data-size={size}
                    data-scrollable={scrollable ? 'true' : 'false'}
                    data-closing={open ? 'false' : 'true'}
                    style={{
                        ...actionPillCssVars(size),
                    }}
                    onKeyDown={handleMenuKeyDown}
                >
                    <ParentMenuContext.Provider value={{ id: menuId, open, rootTriggerRef, rootPlacement, side: parentMenuId && (responsivePlacement === 'left' || responsivePlacement === 'right') ? responsivePlacement : null }}>
                        {options.map(renderOption)}
                    </ParentMenuContext.Provider>
                    {options.length === 0 && emptyLabel && (
                        <span className="ia-action-pill-option ia-action-pill-empty" data-revealed="true">
                            <span className="ia-action-pill-label">{emptyLabel}</span>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

export default ActionPillMenu;
